/**
 * Layout — estructura de página principal del portal (SOA 10 PRO).
 *
 * Desktop (sm+): Sidebar lateral 264px + barra superior de 76px + contenido
 * Mobile (<sm): barra superior mínima + contenido + barra inferior de 92px
 *               con Hoy · Trabajo · Plan · Resultados · Chat + drawer "Más" (Cuenta / Salir)
 *
 * Globales montados:
 *   - ForcePasswordChange (bloquea si pendiente)
 *   - NPSModal
 *   - MoodPrompt
 *   - CommandPalette (Cmd+K)
 *   - OnboardingTour primera vez
 */

import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext.jsx";
import { useState, useEffect } from "react";
import NPSModal from "./NPSModal.jsx";
import ForcePasswordChange from "./ForcePasswordChange.jsx";
import BrandLoader from "./BrandLoader.jsx";
import NotifBell from "./NotifBell.jsx";
import CommandPalette from "./CommandPalette.jsx";
import OnboardingTour from "./OnboardingTour.jsx";
import BienvenidaPortal from "./BienvenidaPortal.jsx";
import Sidebar from "./Sidebar.jsx";
import VoiceMemoFAB from "./VoiceMemoFAB.jsx";
import Icon from "./Icon.jsx";
import { InterruptBudgetProvider } from "../context/InterruptBudget.jsx";
import { ZONAS, zonaDeRuta } from "../lib/navZones.js";

// Barra inferior móvil (2f): Hoy · Trabajo · Plan · Resultados · Chat. Cuenta
// (usada poco) vive en el menú "⋯" junto a Salir; en escritorio las 5 zonas
// del Sidebar no cambian.
const ZONA_CUENTA = ZONAS.find(z => z.key === "cuenta");
const TABS_MOVIL = [
  ...ZONAS.filter(z => z.key !== "cuenta"),
  { key: "chat", to: "/chat", label: "Chat", icon: "chat_bubble", isActive: (p) => p.startsWith("/chat") },
];
import { useSidebarBadges } from "../lib/useSidebarBadges.js";
import { useChatPreview } from "../lib/useChatPreview.js";

const SIDEBAR_COLLAPSED_KEY = "portal_sidebar_collapsed";

const abrirBuscador = () => window.dispatchEvent(new CustomEvent("portal:abrir-buscador"));

export default function Layout() {
  const { clienteActivo, clienteActivoId, clientes, user, loading, logout, elegirCliente } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [masOpen, setMasOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1"; } catch { return false; }
  });
  // Un solo polling de badges / chat para Sidebar (desktop) y barra inferior (mobile).
  const { badges } = useSidebarBadges(clienteActivoId);
  const chatPreview = useChatPreview(clienteActivoId);

  function toggleCollapse() {
    setSidebarCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  }

  // Si no hay cliente activo → auto-seleccionar o redirigir
  // Título de la pestaña del navegador por sección (antes todas decían igual: «Portal Cliente · The Crew»).
  useEffect(() => { const z = zonaDeRuta(pathname); document.title = z ? `${z} · Portal Cliente · THE CREW AGENCIA` : "Portal Cliente · THE CREW AGENCIA"; }, [pathname]);
  useEffect(() => {
    if (loading) return;
    if (clienteActivo) return;
    if (clientes.length === 0) return; // edge: usuario sin clientes
    if (clientes.length === 1) {
      // Auto-seleccionar el único cliente disponible (el portal_cliente_id en localStorage no matcheaba)
      elegirCliente(clientes[0].id);
      return;
    }
    // Múltiples clientes — ir a elegir
    navigate("/elegir-cliente", { replace: true });
  }, [loading, clienteActivo, clientes, navigate, elegirCliente]);

  if (loading) return <BrandLoader mensaje="Cargando tu portal…"/>;

  // Si no hay cliente activo, mostramos algo (no blank screen)
  if (!clienteActivo) {
    if (clientes.length === 0) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[var(--paper)] p-6">
          <div className="bg-[var(--raised)] rounded-[20px] p-8 max-w-md text-center">
            <h2 className="font-display text-[24px] text-[var(--ink)]">Tu cuenta no tiene clientes asignados todavía</h2>
            <p className="text-sm text-[var(--muted)] mt-2">
              Contactá a tu ejecutivo de cuenta de The Crew para que te dé acceso.
            </p>
            <button onClick={logout}
                    className="mt-5 text-xs text-[var(--muted)] hover:text-[var(--ink-2)] underline">
              Cerrar sesión
            </button>
          </div>
        </div>
      );
    }
    return <BrandLoader mensaje="Cargando…"/>; // estamos por auto-seleccionar o redirigir
  }

  const MAIN_MARGIN = sidebarCollapsed ? "sm:ml-20" : "sm:ml-[264px]";
  // --sidebar-w es la única fuente de verdad del ancho del sidebar. Cualquier
  // elemento position:fixed en cualquier pantalla (barras de decisión, etc.)
  // la lee en vez de hardcodear el ancho — así nunca queda descentrado cuando
  // el usuario colapsa el sidebar.
  const sidebarW = sidebarCollapsed ? "5rem" : "16.5rem";

  const trabajoBadge = (badges.tareas_atencion + badges.pedidos_pendientes) || null;
  const zona = zonaDeRuta(pathname);
  const enCuenta = ZONA_CUENTA.isActive(pathname);

  return (
    <InterruptBudgetProvider pathname={pathname}>
    <div className="min-h-screen bg-[var(--paper)]" style={{ "--sidebar-w": sidebarW }}>
      {/* Sidebar desktop */}
      <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={toggleCollapse} badges={badges} chatPreview={chatPreview}/>

      {/* Top bar mobile — logo del cliente (36px, radio 12) + nombre + buscar +
          avisos + "Más". paddingTop safe-area: instalada como PWA
          (viewport-fit=cover), el notch dibuja encima sin este respiro. */}
      <header className="sm:hidden bg-[var(--paper)] sticky top-0 z-20"
              style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="px-[18px] h-14 flex items-center gap-2.5">
          {/* el color de marca del cliente sobrevive solo acá (su avatar) */}
          {clienteActivo.logo_url ? (
            <img src={clienteActivo.logo_url} alt="" className="w-9 h-9 rounded-[12px] object-contain bg-white flex-shrink-0"/>
          ) : (
            <div className="w-9 h-9 rounded-[12px] flex items-center justify-center text-white text-xs font-extrabold flex-shrink-0"
                 style={{ background: clienteActivo.color_primario || "#111318" }}>
              {(clienteActivo.nombre || "?").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0 text-[16px] font-extrabold text-[var(--ink)] truncate">{clienteActivo.nombre}</div>
          {/* búsqueda global en mobile — dispara el keydown sintético que
              CommandPalette escucha globalmente (⌘K no existe en touch). */}
          <button onClick={abrirBuscador}
                  className="w-11 h-11 -mr-1 flex items-center justify-center text-[var(--ink)] flex-shrink-0 no-touch-min"
                  aria-label="Buscar">
            <Icon name="search" size={24}/>
          </button>
          <NotifBell/>
          {/* "Más": Chat y Cerrar sesión no tienen otro lugar en mobile. */}
          <button onClick={() => setMasOpen(true)}
                  className={`relative w-11 h-11 -mr-2 flex items-center justify-center text-[var(--ink)] flex-shrink-0 no-touch-min rounded-full ${enCuenta ? "bg-[var(--line)]" : ""}`}
                  aria-label="Más: Cuenta y salir">
            <Icon name="more_horiz" size={24} fill={enCuenta}/>
          </button>
        </div>
      </header>

      {/* Top bar desktop — 76px: rótulo de la zona · búsqueda en píldora
          (blanca, 46px, máx. 520) · campana + avatar a la derecha. */}
      <header className={`hidden sm:block ${MAIN_MARGIN} transition-[margin] duration-200 bg-[var(--paper)] sticky top-0 z-20`}>
        <div className="max-w-[1400px] mx-auto h-[76px] flex items-center gap-3 pl-2 pr-7">
          {zona && (
            <span className="text-[13px] font-extrabold uppercase tracking-[.06em] text-[var(--faint)] flex-shrink-0">
              {zona}
            </span>
          )}
          <button
            onClick={abrirBuscador}
            className="flex-1 max-w-[520px] ml-4 h-[46px] flex items-center gap-2.5 px-[18px] bg-[var(--surface)] rounded-full text-[15px] text-[var(--muted)] hover:shadow-[0_1px_3px_rgba(17,19,24,0.08)] transition"
          >
            <Icon name="search" size={22}/>
            <span className="flex-1 text-left">Buscar tareas, reportes, docs…</span>
            <kbd className="px-2 py-[3px] bg-[var(--paper)] rounded-lg font-sans text-[12px] font-bold text-[var(--muted)]">⌘K</kbd>
          </button>
          <div className="flex-1"/>
          <NotifBell circle/>
          {/* avatar a la derecha — mismo destino que el menú de usuario del Sidebar */}
          <Link to="/cuenta/configuracion" title={user?.nombre || "Configuración"}
                className="w-[46px] h-[46px] rounded-full bg-[var(--ink)] flex items-center justify-center text-[var(--surface)] text-[15px] font-extrabold flex-shrink-0 hover:opacity-85 transition-opacity">
            {(user?.nombre || user?.email || "?").slice(0, 1).toUpperCase()}
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className={`${MAIN_MARGIN} transition-[margin] duration-200`}>
        {/* pb-28 en mobile es el despeje funcional de la barra inferior fija
            (92px + safe area). */}
        <div className="max-w-[1400px] mx-auto px-[18px] sm:pl-2 sm:pr-7 pt-3 sm:pt-1 pb-28 sm:pb-28">
          <Outlet />
        </div>
      </main>

      {/* Barra inferior mobile — 92px, Hoy · Trabajo · Plan · Resultados · Chat. La
          zona activa lleva píldora #E7E6E1 de 60×34, ícono relleno y etiqueta
          en 800 (bug 10: antes ninguna pestaña se marcaba). El aviso es una
          píldora #E11D48. paddingBottom safe-area para el home indicator. */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[var(--surface)] border-t border-[var(--line)] z-[var(--z-nav)] sm:hidden"
           style={{ height: "calc(92px + env(safe-area-inset-bottom))", paddingBottom: "env(safe-area-inset-bottom)" }}
           aria-label="Zonas del portal">
        <div className="flex px-1 pt-[10px]">
          {TABS_MOVIL.map(z => (
            <NavTab key={z.key} zona={z} active={z.isActive(pathname)}
                    badge={z.key === "trabajo" ? trabajoBadge
                         : z.key === "chat" && chatPreview?.unread && !z.isActive(pathname) ? (chatPreview.unreadCount > 9 ? "9+" : chatPreview.unreadCount || 1)
                         : null}
                    dot={z.key === "plan" ? badges.plan_nuevo : z.key === "resultados" ? badges.reporte_nuevo : false}/>
          ))}
        </div>
      </nav>

      {/* Drawer "Más" mobile — Cuenta (fuera de la barra inferior, que lleva
          Chat) y Cerrar sesión. Es un overlay: única superficie con sombra. */}
      {masOpen && (
        <div className="fixed inset-0 z-[var(--z-sheet)] sm:hidden" onClick={() => setMasOpen(false)}>
          <div className="absolute inset-0 bg-[var(--ink)]/40"/>
          <div className="shadow-overlay absolute bottom-0 left-0 right-0 bg-[var(--surface)] rounded-t-[32px] p-4 pt-3 animate-slideUp max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}
               style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
            <div className="w-12 h-1.5 bg-[var(--line-control)] rounded-full mx-auto mb-4"/>
            <div className="grid grid-cols-2 gap-3">
              <MasMobile to={ZONA_CUENTA.to} icon="settings" label="Cuenta" onClick={() => setMasOpen(false)} color="blue"/>
              <button onClick={() => { setMasOpen(false); logout(); }}
                      className="aspect-square rounded-[20px] bg-[var(--danger-soft)] hover:opacity-80 flex flex-col items-center justify-center gap-1.5 text-[var(--danger)]">
                <LogOut className="h-5 w-5"/>
                <span className="text-[length:var(--text-nav)] font-extrabold">Salir</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Globales — MoodPrompt vive como franja al pie de Hoy (Dashboard.jsx). */}
      <ForcePasswordChange/>
      <NPSModal/>
      <CommandPalette/>
      <BienvenidaPortal/>
      <OnboardingTour/>

      {/* Voice memo FAB — siempre visible para que el cliente pida con voz */}
      <VoiceMemoFAB/>
    </div>
    </InterruptBudgetProvider>
  );
}

function NavTab({ zona, active, badge, dot }) {
  // data-tour: mismo hook que Sidebar.jsx para que OnboardingTour encuentre
  // un destino real sin importar el viewport.
  return (
    <Link to={zona.to} data-tour={zona.tour} aria-current={active ? "page" : undefined}
          className="flex-1 min-w-0 flex flex-col items-center gap-1 no-touch-min">
      <span className={`relative w-[60px] h-[34px] rounded-full flex items-center justify-center transition-colors ${active ? "bg-[var(--line)]" : ""}`}>
        <Icon name={zona.icon} size={24} fill={active} className={active ? "text-[var(--ink)]" : "text-[var(--muted)]"}/>
        {badge ? (
          <span className="absolute -top-1 right-0.5 min-w-[18px] h-[18px] px-[5px] rounded-full bg-[var(--notif)] text-white text-[12px] font-extrabold flex items-center justify-center">
            {badge}
          </span>
        ) : dot ? (
          <span className="absolute top-0 right-2.5 w-2 h-2 rounded-full bg-[var(--notif)]"/>
        ) : null}
      </span>
      <span className={`text-[12px] ${active ? "font-extrabold text-[var(--ink)]" : "font-semibold text-[var(--muted)]"}`}>{zona.label}</span>
    </Link>
  );
}

function MasMobile({ to, icon, label, onClick, color = "slate" }) {
  // Mapa de categorías (tiles del drawer "Más") — todo converge a los tokens
  // de la piel (neutral/accent/attention/done).
  const colors = {
    slate:   "bg-[var(--panel)] text-[var(--ink-2)]",
    purple:  "bg-[var(--accent-soft)] text-[var(--accent)]",
    fuchsia: "bg-[var(--accent-soft)] text-[var(--accent)]",
    blue:    "bg-[var(--accent-soft)] text-[var(--accent)]",
    amber:   "bg-[var(--attention-soft)] text-[var(--attention-ink)]",
    emerald: "bg-[var(--done-soft)] text-[var(--done)]",
  };
  return (
    <Link to={to} onClick={onClick}
          className={`aspect-square rounded-[20px] ${colors[color]} flex flex-col items-center justify-center gap-1.5`}>
      <Icon name={icon} size={22}/>
      <span className="text-[length:var(--text-nav)] font-extrabold">{label}</span>
    </Link>
  );
}
